import { useMemo, useState } from 'react';
import type { FinderContextValue } from '@frameio/assets/shared';
import { useActiveProjectId } from '@frameio/shared/contexts';
import type { DropZoneProps } from '@frameio/vapor';
import { useUploadWorkerQueue } from './useUploadWorkerQueue';

type UseUploadsReturns = {
  transfersDnd: FinderContextValue['transfersDnd'];
  isCreating: boolean;
  uploadFiles: DropZoneProps['onFilesReceived'];
};

/**
 * Packages the uploader callback for uploads into the current folder, or into
 * a hovered folder.
 *
 * @returns hoverBindings The callbacks for folder hover
 * @returns hoverFolderId The currently hovered folder id if hovering
 * @returns isCreating Whether assets are being created in the current folder
 * @returns uploadFiles The uploadFiles callback from the useUploadWorkerQueue
 */
export function useUploads(
  currentFolderId: string,
  isCreating: boolean = false
): UseUploadsReturns {
  const projectId = useActiveProjectId();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const uploadFiles = useUploadWorkerQueue({
    uploadType: 'asset',
    folderId: hoveredId ?? currentFolderId,
    projectId,
    scanFileSystemEntries: true,
  });

  return useMemo(
    () => ({
      transfersDnd: {
        hoveredId,
        onTransferDrop: async (droppedFolderId, dataTransfer) => {
          setHoveredId(droppedFolderId);
          try {
            await uploadFiles(dataTransfer);
          } catch (error) {
            // console.error(error);
          } finally {
            setHoveredId(null);
          }
        },
        setHoveredId,
      },
      isCreating,
      uploadFiles,
    }),
    [hoveredId, isCreating, uploadFiles]
  );
}
